import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "@/shared/lib";

import { DialogOverlay, DialogPortal } from "../dialog";

const SIDES = {
  left: cn(
    "inset-y-0 left-0 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
  ),
  right: cn(
    "inset-y-0 right-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  ),
  // Снизу — по высоте содержимого, во всю ширину, со скруглённым верхом (описание предмета на телефоне).
  bottom: cn(
    "inset-x-0 bottom-0 h-auto max-h-[85dvh] w-full max-w-none overflow-y-auto rounded-t-2xl border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
  ),
};

type SheetContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  side?: keyof typeof SIDES;
  showCloseButton?: boolean;
};

export const SheetContent = ({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}: SheetContentProps) => {
  const { _ } = useLingui();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "fixed z-50 flex h-full w-3/4 max-w-sm flex-col border-border bg-surface text-foreground shadow-[0_16px_40px_rgb(0_0_0/0.55)] transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:animate-in data-[state=open]:duration-300",
          SIDES[side],
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="sheet-close"
            aria-label={_(msg`Close`)}
            className="absolute top-4 right-4 rounded-sm text-muted transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent/60 [&_svg]:size-4"
          >
            <XIcon aria-hidden="true" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
